import { IconButton, Stack } from '@mui/material';
import { ContextMenu, SlidersIcon } from '@schooly/style';
import { Dispatch, FC, SetStateAction, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { MarkbookOverrideValueAndDisable, MarkbookViewSettings } from './MarkbookSettings';
import { MarkbookSettingsSwitch } from './MarkbookSettingsSwitch';

export interface DropdownMarkbookSettingsProps {
  settings: MarkbookViewSettings;
  onSetSettings: Dispatch<SetStateAction<MarkbookViewSettings>>;
  overrideValueAndDisable: MarkbookOverrideValueAndDisable;
}

export const DropdownMarkbookSettings: FC<DropdownMarkbookSettingsProps> = ({
  settings,
  onSetSettings,
  overrideValueAndDisable,
}) => {
  const { formatMessage } = useIntl();

  const updateSettings = useCallback(
    (key: keyof MarkbookViewSettings, value: boolean) => {
      onSetSettings((prev) => ({ ...prev, [key]: value }));
    },
    [onSetSettings],
  );

  const avgResultsLabel = formatMessage({ id: 'assessments-markbook-AvgResults' });
  const coloredCellsLabel = formatMessage({ id: 'assessments-markbook-ColoredCells' });
  const scoreInPercentageLabel = formatMessage({ id: 'assessments-markbook-ScoreInPercentage' });

  const isAvgResultsDisabled = overrideValueAndDisable.avgResults !== undefined;
  const isColoredCellsDisabled = overrideValueAndDisable.coloredCells !== undefined;
  const isScoreInPercentageDisabled = overrideValueAndDisable.scoreInPercentage !== undefined;

  return (
    <ContextMenu
      placement="bottom"
      arrow={false}
      componentsProps={{ tooltip: { sx: { p: 1, minWidth: 230, '.container': { p: 0 } } } }}
      slotProps={{
        popper: {
          sx: (theme) => ({
            '&.MuiTooltip-popper[data-popper-placement*="bottom"] .MuiTooltip-tooltip': {
              marginTop: theme.spacing(0.25),
            },
          }),
        },
      }}
      renderMenuContent={() => (
        <Stack gap={0.5}>
          <MarkbookSettingsSwitch
            disabled={isAvgResultsDisabled}
            label={avgResultsLabel}
            value={
              isAvgResultsDisabled ? !!overrideValueAndDisable.avgResults : settings.avgResults
            }
            onChange={(value) => {
              updateSettings('avgResults', value);
              if (value && !isColoredCellsDisabled) {
                updateSettings('coloredCells', true);
              }
            }}
            sx={{ p: 0.5 }}
          />

          <MarkbookSettingsSwitch
            disabled={isColoredCellsDisabled}
            label={coloredCellsLabel}
            value={
              isColoredCellsDisabled
                ? !!overrideValueAndDisable.coloredCells
                : settings.coloredCells
            }
            onChange={(value) => updateSettings('coloredCells', value)}
            sx={{ p: 0.5 }}
          />

          <MarkbookSettingsSwitch
            disabled={isScoreInPercentageDisabled}
            label={scoreInPercentageLabel}
            value={
              isScoreInPercentageDisabled
                ? !!overrideValueAndDisable.scoreInPercentage
                : settings.scoreInPercentage
            }
            onChange={(value) => updateSettings('scoreInPercentage', value)}
            sx={{ p: 0.5 }}
          />
        </Stack>
      )}
    >
      {(open, opened) => (
        <IconButton
          onClick={open}
          sx={(theme) => ({
            '&.MuiIconButton-root': {
              color: opened ? 'background.paper' : undefined,
            },
            backgroundColor: opened ? 'primary.main' : undefined,
            borderRadius: theme.spacing(0.5),
            width: 30,
            height: 30,
          })}
        >
          <SlidersIcon />
        </IconButton>
      )}
    </ContextMenu>
  );
};
